// One evolving, spherical weather field shared by the globe, orbital patch
// and volumetric cloud renderer. A density reference seeds evolving weather;
// the procedural regional model remains available while it loads.
import * as THREE from 'three';
import { EARTH_WEATHER_FRAGMENT } from './shaders/earthweather.js';
import { buildCloudNoise, getCloudNoise } from './rocketry/cloudnoise.js';
import { compileFor } from './gpuwarm.js';
const fields=new WeakMap();
// The globe's own handle on the cloud noise volumes (its cloud tops from
// orbit read them, src/shaders/earthcloudtop.js), kept for the session.
const noiseHandles=new WeakMap();
const WIDTH=4096;
// near: the camera is close enough to Earth for the cloud map's texels to
// be magnified, so the globe wants the volumes its cloud tops are carved
// from (baked once per renderer, shared with the volumetric clouds).
function fieldFor(renderer){
 let f=fields.get(renderer);
 if(!f){
  const width=Math.min(WIDTH,renderer.capabilities.maxTextureSize,renderer.domElement.width<=1024?2048:WIDTH);
  const target=new THREE.WebGLRenderTarget(width,width/2,{depthBuffer:false,stencilBuffer:false,wrapS:THREE.RepeatWrapping,minFilter:THREE.LinearMipmapLinearFilter,magFilter:THREE.LinearFilter,generateMipmaps:true});
  target.texture.name='Dynamic spherical Earth weather';
  const material=new THREE.ShaderMaterial({uniforms:{uTime:{value:0},uOcean:{value:null},uOceanOn:{value:0},uGuide:{value:null},uGuideBlend:{value:0}},vertexShader:'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.,1.);}',fragmentShader:EARTH_WEATHER_FRAGMENT,depthTest:false,depthWrite:false});
  const scene=new THREE.Scene(),quad=new THREE.Mesh(new THREE.PlaneGeometry(2,2),material);quad.frustumCulled=false;scene.add(quad);
  f={target,material,scene,camera:new THREE.Camera(),last:-Infinity,guideSince:null};fields.set(renderer,f);
 }
 return f;
}
// The field's shader, compiled before the first frame draws it (the viewer's
// loading screen waits for it, src/gpuwarm.js): made in that frame, the frame
// waited on the driver while it compiled.
export function prepareEarthWeather(renderer){
 if(!renderer||renderer.xr.isPresenting)return;
 const f=fieldFor(renderer);
 compileFor(renderer,f.scene,f.camera,f.scene);
}
export function updateEarthWeather(renderer,mesh,now,near=false){
 if(!mesh?.material?.uniforms?.cloudsTexture||renderer.xr.isPresenting)return;
 const gu=mesh.material.uniforms;
 if(gu.uCloudOrbitVol){
  let noise=getCloudNoise(renderer);
  if(!noise&&near){noiseHandles.set(renderer,buildCloudNoise(renderer));noise=getCloudNoise(renderer);}
  gu.uCloudOrbitVol.value=noise?noise.orbit.texture:null;
  gu.uCloudVolOn.value=noise?1:0;
 }
 const f=fieldFor(renderer);
 const guide=mesh.material.uniforms.weatherGuideTexture?.value;
 if(guide&&f.guideSince===null)f.guideSince=Number.isFinite(f.last)?now:now-2500;
 const guideBlend=guide?THREE.MathUtils.smoothstep(now-f.guideSince,0,2500):0;
 if(now-f.last>=(guideBlend>0&&guideBlend<1?100:1000)){
  f.last=now;f.material.uniforms.uTime.value=now/1000;
  const ocean=mesh.material.uniforms.specularTexture?.value;
  f.material.uniforms.uOcean.value=ocean||null;
  f.material.uniforms.uOceanOn.value=ocean?1:0;
  f.material.uniforms.uGuide.value=guide||null;
  f.material.uniforms.uGuideBlend.value=guideBlend;
  const target=renderer.getRenderTarget(),face=renderer.getActiveCubeFace(),mip=renderer.getActiveMipmapLevel(),auto=renderer.autoClear;
  try{renderer.autoClear=true;renderer.setRenderTarget(f.target);renderer.render(f.scene,f.camera);}
  finally{renderer.setRenderTarget(target,face,mip);renderer.autoClear=auto;}
 }
 // The globe keeps drawing the full-resolution reference (cloudsTexture) at
 // the field's own address; the half-resolution field feeds the volumes.
 // The field is re-rendered once a second, and a crisp cloud-top edge moved
 // 20 m at once ticks visibly from low orbit, so the globe's clock runs on
 // between those steps. Every other reader of the weather's time (rocketry's
 // orbital shell, the viewer's volume) takes this value, so all of them sit
 // on one clock.
 if(mesh.material.uniforms.weatherTime){
  const t=f.material.uniforms.uTime.value;
  mesh.material.uniforms.weatherTime.value=t+THREE.MathUtils.clamp(now/1000-t,0,1);
 }
 return f.target.texture;
}

export function getEarthWeather(renderer) { return fields.get(renderer)?.target.texture || null; }
